import React, { useEffect, useState } from "react";
import { FormInstance } from "@payping/web-components/components/Form/types";
import { Message, Select } from "@payping/web-components";
import { OptionType } from "@payping/web-components/components/Select/sub/types";
import { ValidEmail, ValidMobileNumber } from "@payping/utils/validation";
import { InvoiceCustomerDetailViewModel, customerApis } from "@payping/api";
import { useAsync, useAsyncFn, useDebounce } from "react-use";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { getCustomerLabel } from "./customer";
import { selectCustomers } from "./selectCustomers";

interface SelectCustomerProps {
  setCreateCustomerModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  customerList: OptionType[] | undefined;
  setCustomerList: React.Dispatch<React.SetStateAction<OptionType[] | undefined>>;
  onCreateCustomer: (customer: OptionType) => void;
  form: FormInstance;
  selectedCategories?: string[];
  value?: OptionType[];
  skeleton?: boolean;
}

const SelectCustomer: React.FC<SelectCustomerProps> = (props: SelectCustomerProps) => {
  const {
    setCreateCustomerModalVisible,
    customerList,
    setCustomerList,
    onCreateCustomer,
    form,
    skeleton,
    ...otherProps
  } = props;
  const [customerSearch, setCustomerSearch] = useState<string>();
  const [customerLoading, setCustomerLoading] = React.useState<boolean>();
  const [inlineCreateError, setInlineCreateError] = React.useState<string>();
  const { t } = useAppTranslation();

  const [, fetchOptions] = useAsyncFn(async () => {
    if (customerSearch || !customerList?.length) {
      const response = await customerApis.customerList(0, 5, customerSearch, undefined, true);
      setCustomerList(
        response?.map((value) => {
          const title = value.phone || value.email || value.code;
          const label = getCustomerLabel(value);
          return {
            avatar: value.userPhoto,
            email: value.email,
            key: value.code,
            label,
            phone: value.phone,
            title,
            value: value.code || "",
          };
        }),
      );
      setCustomerLoading(false);
      // response?.map((value) => selectCustomers(value));
    }
  }, [customerSearch]);

  const [, handleInlineCreation] = useAsyncFn(async (value: string) => {
    const emailError = ValidEmail(value) ? undefined : t("invoice.create.base.error.email");
    const phoneError = ValidMobileNumber(value) ? undefined : t("invoice.create.base.error.phone");
    const defaultError = t("invoice.create.base.error.phoneOrEmail");
    let foundCustomer: InvoiceCustomerDetailViewModel | undefined;
    if (!phoneError) {
      foundCustomer = await customerApis.customerCreateOrFind({ phone: value });
    } else if (!emailError) {
      foundCustomer = await customerApis.customerCreateOrFind({ email: value });
    } else {
      setInlineCreateError(phoneError || emailError || defaultError);
    }
    if (
      (!phoneError || !emailError) &&
      foundCustomer &&
      !form.getFieldValue("customers")?.find((customer: OptionType) => customer.value === foundCustomer?.code)
    ) {
      onCreateCustomer(selectCustomers(foundCustomer));
      setCustomerSearch("");
    }
  }, []);
  useEffect(() => {
    if (inlineCreateError) {
      Message.error({ content: inlineCreateError });
    }
  }, [inlineCreateError]);

  const debounceTimeout = 1000;
  useDebounce(fetchOptions, debounceTimeout, [fetchOptions]);

  const { value: customerTags } = useAsync(customerApis.customerTagGet);

  return (
    <Select.Create
      className="background--white"
      filterOption={false}
      create={{
        label: t("invoice.create.base.createCustomerButton"),
        onClick: (): void => {
          setCreateCustomerModalVisible(true);
        },
      }}
      category={{
        filterOption: (input, option): boolean => (option?.title as unknown as string).indexOf(input) >= 0,
        label: t("invoice.create.base.customerCategories"),
        options: customerTags?.map(({ id, title }) => ({
          key: id,
          label: title,
          title,
          type: "category",
          value: title,
        })),
      }}
      onSearch={(value?: string) => {
        setCustomerList([]);
        setCustomerLoading(true);
        setCustomerSearch(value);
      }}
      loading={customerLoading}
      options={customerList}
      allowClear
      placeholder={t("invoice.create.base.payers")}
      searchValue={customerSearch}
      inlineCreate={handleInlineCreation}
      skeleton={skeleton}
      {...otherProps}
    />
  );
};

export default SelectCustomer;
